import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeftRight, Info } from 'lucide-react';

interface Insurer { id: string; name: string; active: boolean; city: string }

export const MasterSwitch: React.FC = () => {
  const [rows, setRows] = useState<Insurer[]>([]);
  const { switchIntoInsurer } = useAuth();
  const nav = useNavigate();
  useEffect(() => { api.get<Insurer[]>('/master/insurers').then((r) => setRows(r.data)); }, []);

  const doSwitch = async (id: string, name: string): Promise<void> => {
    if (!confirm(`Switch into ${name} (${id})?\n\nYou will be operating inside this insurer's portal as their Super Admin. Every action you take is recorded in the audit log against your Master Admin identity.`)) return;
    try { await switchIntoInsurer(id); toast.success(`Now operating as ${id}`); nav('/insurer', { replace: true }); }
    catch (err: any) { toast.error(err?.response?.data?.error || 'Switch failed.'); }
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="master-switch-page">
      <div>
        <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Switch into insurer</h2>
        <p className="text-sm text-raptor-navy/60 font-body">Assume an insurer's Super Admin identity to help with configuration or operational work. Actions taken inside the insurer portal remain attributed to your Master Admin account for audit purposes.</p>
      </div>
      <Card className="border-raptor-blue/30 bg-raptor-blue/5">
        <CardContent className="p-4 flex items-start gap-3">
          <Info size={20} className="text-raptor-blue mt-0.5" />
          <div className="text-sm font-body text-raptor-navy/80">
            While you are switched in, the header bar will show an <b>Impersonating</b> badge in the insurer portal. Click <b>Return to Master Portal</b> in that header at any time to come straight back here — no need to sign out.
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Available insurers · {rows.length}</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-auto">
          <table className="w-full text-sm font-body">
            <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
              <tr>
                <th className="py-2 px-3 text-left">ID</th>
                <th className="py-2 px-3 text-left">Name</th>
                <th className="py-2 px-3 text-left">City</th>
                <th className="py-2 px-3 text-left">Status</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/60">
                  <td className="py-2 px-3 font-tabular text-xs">{r.id}</td>
                  <td className="py-2 px-3"><b>{r.name}</b></td>
                  <td className="py-2 px-3">{r.city || '—'}</td>
                  <td className="py-2 px-3">{r.active ? <Badge variant="success">Active</Badge> : <Badge variant="muted">Inactive</Badge>}</td>
                  <td className="py-2 px-3 text-right"><Button size="sm" variant="gradient" disabled={!r.active} onClick={() => doSwitch(r.id, r.name)} data-testid={`master-switch-${r.id}`}><ArrowLeftRight size={14} /> Switch in</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};
